import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Button,
  Space,
  Tag,
  Modal,
  Form,
  Input,
  Select,
  message,
  Row,
  Col,
  Statistic,
  Alert,
  Typography,
  Popconfirm,
  Tabs,
  Badge,
  Tooltip,
  Divider
} from 'antd';
import {
  KeyOutlined,
  PlusOutlined,
  EyeOutlined,
  EyeInvisibleOutlined,
  CopyOutlined,
  DeleteOutlined,
  ReloadOutlined,
  EditOutlined,
  LockOutlined,
  UnlockOutlined,
  SafetyOutlined,
  ExclamationCircleOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  SettingOutlined,
  ApiOutlined,
  UploadOutlined,
  SyncOutlined,
  DownloadOutlined
} from '@ant-design/icons';
import axios from 'axios';

const { Option } = Select;
const { Text, Paragraph, Title } = Typography;
const { TabPane } = Tabs;

const MerchantSnapV2Credentials = () => {
  const [loading, setLoading] = useState(false);
  const [credentials, setCredentials] = useState([]);
  const [merchantInfo, setMerchantInfo] = useState(null);
  const [selectedCredential, setSelectedCredential] = useState(null);
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [regenerateModalVisible, setRegenerateModalVisible] = useState(false);
  const [createModalVisible, setCreateModalVisible] = useState(false);
  const [uploadKeyModalVisible, setUploadKeyModalVisible] = useState(false);
  const [regenerateKeypairModalVisible, setRegenerateKeypairModalVisible] = useState(false);
  const [createdCredentials, setCreatedCredentials] = useState(null);
  const [showSecrets, setShowSecrets] = useState({});
  const [form] = Form.useForm();
  const [createForm] = Form.useForm();
  const [uploadKeyForm] = Form.useForm();

  // Get merchant info from localStorage
  const [merchantData, setMerchantData] = useState(null);
  const [memberId, setMemberId] = useState(null);

  useEffect(() => {
    try {
      const storedData = localStorage.getItem('merchant_user');
      if (storedData) {
        const parsed = JSON.parse(storedData);
        setMerchantData(parsed);
        setMemberId(parsed.id || parsed.member_id);
      } else {
        // For development/testing - use test member ID
        setMemberId(2);
      }
    } catch (error) {
      console.error('Error parsing merchant data:', error);
      setMemberId(2);
    }
  }, []);

  useEffect(() => {
    if (memberId) {
      fetchSnapV2Credentials();
    }
  }, [memberId]);

  const fetchSnapV2Credentials = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('merchant_access_token');
      if (!token) {
        message.error('Token tidak ditemukan. Silakan login kembali.');
        return;
      }

      const response = await axios.get(`/api/merchant/snap-v2-credentials`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      console.log('SNAP V2 Credentials Response:', response.data);
      setCredentials(response.data.credentials || []);
      setMerchantInfo(response.data.merchant_info);

    } catch (error) {
      message.error('Gagal memuat SNAP BI V2 credentials');
      console.error('Fetch SNAP V2 credentials error:', error);
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text, type = 'text') => {
    navigator.clipboard.writeText(text).then(() => {
      if (type === 'partner_id') {
        message.success('Partner ID berhasil disalin! 🏪');
      } else if (type === 'client_id') {
        message.success('Client ID berhasil disalin! 🔑');
      } else if (type === 'client_secret') {
        message.success('Client Secret berhasil disalin! 🔐');
      } else {
        message.success('Berhasil disalin! 📋');
      }
    }).catch(err => {
      console.error('Gagal menyalin:', err);
      message.error('Gagal menyalin!');
    });
  };

  const downloadAsPem = (content, filename, type = 'key') => {
    try {
      const blob = new Blob([content], { type: 'application/x-pem-file' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      message.success(`${type === 'private' ? 'Private Key' : 'Public Key'} berhasil didownload! 📥`);
    } catch (err) {
      console.error('Gagal download:', err);
      message.error('Gagal download file!');
    }
  };

  const viewCredentialDetail = async (credential) => {
    try {
      const token = localStorage.getItem('merchant_access_token');
      const response = await axios.get(
        `/api/merchant/snap-v2-credentials/${credential.id}`,
        {
          headers: { Authorization: `Bearer ${token}` },
          params: { revealSecret: false }
        }
      );

      setSelectedCredential(response.data.credential);
      setDetailModalVisible(true);
    } catch (error) {
      message.error('Gagal memuat detail credential');
    }
  };

  const regenerateClientSecret = async (credential) => {
    setSelectedCredential(credential);
    setRegenerateModalVisible(true);
  };

  const handleRegenerateSecret = async () => {
    try {
      const token = localStorage.getItem('merchant_access_token');
      const response = await axios.post(
        `/api/merchant/snap-v2-credentials/${selectedCredential.id}/regenerate-secret`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      Modal.success({
        title: 'Client Secret Berhasil Diperbarui',
        content: (
          <div>
            <Alert
              message="Simpan Client Secret dengan aman!"
              description={
                <div>
                  <p><strong>Client Secret Baru:</strong></p>
                  <Input.TextArea
                    value={response.data.new_client_secret}
                    readOnly
                    autoSize={{ minRows: 2 }}
                    style={{ fontFamily: 'monospace', marginBottom: 8 }}
                  />
                  <Text type="secondary">
                    {response.data.warning}
                  </Text>
                </div>
              }
              type="warning"
              showIcon
              style={{ marginBottom: 16 }}
            />
          </div>
        ),
        width: 600
      });

      setRegenerateModalVisible(false);
      setSelectedCredential(null);
      fetchSnapV2Credentials();
    } catch (error) {
      message.error('Gagal memperbarui client secret');
    }
  };

  const openUploadKeyModal = (credential) => {
    setSelectedCredential(credential);
    uploadKeyForm.resetFields();
    setUploadKeyModalVisible(true);
  };

  const handleUploadPublicKey = async (values) => {
    try {
      const token = localStorage.getItem('merchant_access_token');
      await axios.put(
        `/api/merchant/snap-v2-credentials/${selectedCredential.id}/public-key`,
        { public_key: values.public_key },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      message.success('Public key berhasil diperbarui!');
      setUploadKeyModalVisible(false);
      setSelectedCredential(null);
      uploadKeyForm.resetFields();
      fetchSnapV2Credentials();
    } catch (error) {
      message.error(error.response?.data?.error || 'Gagal memperbarui public key');
    }
  };

  const openRegenerateKeypairModal = (credential) => {
    setSelectedCredential(credential);
    setRegenerateKeypairModalVisible(true);
  };

  const handleRegenerateKeypair = async () => {
    try {
      const token = localStorage.getItem('merchant_access_token');
      const response = await axios.post(
        `/api/merchant/snap-v2-credentials/${selectedCredential.id}/regenerate-keypair`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      Modal.success({
        title: 'RSA Keypair Berhasil Digenerate Ulang',
        content: (
          <div style={{ maxHeight: '60vh', overflowY: 'auto' }}>
            <Alert
              message="Simpan Private Key dengan Aman!"
              description={response.data.warning}
              type="warning"
              showIcon
              style={{ marginBottom: 16 }}
            />

            <div style={{ marginBottom: 16 }}>
              <Text strong>Private Key Baru:</Text>
              <Input.TextArea
                value={response.data.private_key}
                readOnly
                autoSize={{ minRows: 6 }}
                style={{ fontFamily: 'monospace', marginTop: 8, fontSize: '10px' }}
              />
              <Space style={{ marginTop: 8 }}>
                <Button
                  size="small"
                  icon={<CopyOutlined />}
                  onClick={() => copyToClipboard(response.data.private_key)}
                >
                  Copy Private Key
                </Button>
                <Button
                  size="small"
                  type="primary"
                  icon={<DownloadOutlined />}
                  onClick={() => downloadAsPem(
                    response.data.private_key,
                    `${selectedCredential.partner_id}_private_key.pem`,
                    'private'
                  )}
                >
                  Download .pem
                </Button>
              </Space>
            </div>

            <div>
              <Text strong>Public Key Baru:</Text>
              <Input.TextArea
                value={response.data.public_key}
                readOnly
                autoSize={{ minRows: 4 }}
                style={{ fontFamily: 'monospace', marginTop: 8, fontSize: '10px' }}
              />
              <Space style={{ marginTop: 8 }}>
                <Button
                  size="small"
                  icon={<CopyOutlined />}
                  onClick={() => copyToClipboard(response.data.public_key)}
                >
                  Copy Public Key
                </Button>
                <Button
                  size="small"
                  type="primary"
                  icon={<DownloadOutlined />}
                  onClick={() => downloadAsPem(
                    response.data.public_key,
                    `${selectedCredential.partner_id}_public_key.pem`,
                    'public'
                  )}
                >
                  Download .pem
                </Button>
              </Space>
            </div>
          </div>
        ),
        width: 700,
        okText: "Saya Sudah Simpan"
      });

      setRegenerateKeypairModalVisible(false);
      setSelectedCredential(null);
      fetchSnapV2Credentials();
    } catch (error) {
      message.error(error.response?.data?.error || 'Gagal regenerate keypair');
    }
  };

  const handleCreateCredential = () => {
    createForm.resetFields();
    setCreateModalVisible(true);
  };

  const handleCreateSubmit = async (values) => {
    try {
      const token = localStorage.getItem('merchant_access_token');

      setLoading(true);
      const response = await axios.post(
        `/api/merchant/snap-v2-credentials`,
        values,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      // Store created credentials to show in success modal
      setCreatedCredentials(response.data.credentials);
      setCreateModalVisible(false);

      // Show success modal with credentials
      Modal.success({
        title: '✅ SNAP BI V2 Credentials Berhasil Dibuat!',
        content: (
          <div style={{ maxHeight: '60vh', overflowY: 'auto' }}>
            <Alert
              message="⚠️ Simpan Kredensial dengan Aman!"
              description={
                <div>
                  {response.data.warning.map((warning, index) => (
                    <p key={index}>• {warning}</p>
                  ))}
                </div>
              }
              type="warning"
              showIcon
              style={{ marginBottom: 16 }}
            />

            <div style={{ marginBottom: 16 }}>
              <Text strong>📝 Informasi Credential:</Text>
              <div style={{ marginTop: 8 }}>
                <p><strong>Partner ID:</strong> <Text code>{response.data.credentials.partner_id}</Text></p>
                <p><strong>Client ID:</strong> <Text code>{response.data.credentials.client_id}</Text></p>
              </div>
            </div>

            <div style={{ marginBottom: 16 }}>
              <Text strong>🔑 Client Secret:</Text>
              <Input.TextArea
                value={response.data.credentials.client_secret}
                readOnly
                autoSize={{ minRows: 3 }}
                style={{ fontFamily: 'monospace', marginTop: 8 }}
              />
              <Button
                size="small"
                icon={<CopyOutlined />}
                onClick={() => copyToClipboard(response.data.credentials.client_secret, 'client_secret')}
                style={{ marginTop: 8 }}
              >
                Copy Client Secret
              </Button>
            </div>

            <div style={{ marginBottom: 16 }}>
              <Text strong>🔐 Private Key:</Text>
              <Input.TextArea
                value={response.data.credentials.private_key}
                readOnly
                autoSize={{ minRows: 6 }}
                style={{ fontFamily: 'monospace', marginTop: 8, fontSize: '10px' }}
              />
              <Space style={{ marginTop: 8 }}>
                <Button
                  size="small"
                  icon={<CopyOutlined />}
                  onClick={() => copyToClipboard(response.data.credentials.private_key)}
                >
                  Copy Private Key
                </Button>
                <Button
                  size="small"
                  type="primary"
                  icon={<DownloadOutlined />}
                  onClick={() => downloadAsPem(
                    response.data.credentials.private_key,
                    `${response.data.credentials.partner_id}_private_key.pem`,
                    'private'
                  )}
                >
                  Download .pem
                </Button>
              </Space>
            </div>

            <div style={{ marginBottom: 16 }}>
              <Text strong>📢 Public Key:</Text>
              <Input.TextArea
                value={response.data.credentials.public_key}
                readOnly
                autoSize={{ minRows: 4 }}
                style={{ fontFamily: 'monospace', marginTop: 8, fontSize: '10px' }}
              />
              <Space style={{ marginTop: 8 }}>
                <Button
                  size="small"
                  icon={<CopyOutlined />}
                  onClick={() => copyToClipboard(response.data.credentials.public_key)}
                >
                  Copy Public Key
                </Button>
                <Button
                  size="small"
                  type="primary"
                  icon={<DownloadOutlined />}
                  onClick={() => downloadAsPem(
                    response.data.credentials.public_key,
                    `${response.data.credentials.partner_id}_public_key.pem`,
                    'public'
                  )}
                >
                  Download .pem
                </Button>
              </Space>
            </div>

            <div>
              <Text strong>🔗 Callback URL:</Text>
              <p><Text code>{response.data.credentials.callback_url}</Text></p>
            </div>
          </div>
        ),
        width: 800,
        okText: "Saya Sudah Simpan",
        onOk: () => {
          fetchSnapV2Credentials();
          setCreatedCredentials(null);
        }
      });

    } catch (error) {
      console.error('Create credential error:', error);
      message.error(error.response?.data?.error || 'Gagal membuat kredensial');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'configured': return 'green';
      case 'not_configured': return 'orange';
      default: return 'default';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'configured': return 'Terkonfigurasi';
      case 'not_configured': return 'Belum Terkonfigurasi';
      default: return status;
    }
  };

  const columns = [
    {
      title: 'Partner ID',
      dataIndex: 'partner_id',
      key: 'partner_id',
      width: 150,
      render: (partnerId) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Text code style={{ flex: 1, color: '#1890ff', fontSize: '12px' }}>
            {partnerId}
          </Text>
          <Button
            size="small"
            icon={<CopyOutlined />}
            onClick={() => copyToClipboard(partnerId, 'partner_id')}
            title="Copy Partner ID"
          />
        </div>
      ),
    },
    {
      title: 'Client ID',
      dataIndex: 'client_id',
      key: 'client_id',
      width: 200,
      render: (clientId) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Text code style={{ flex: 1, fontSize: '12px' }}>
            {clientId?.length > 15 ? `${clientId.substring(0, 15)}...` : clientId}
          </Text>
          <Button
            size="small"
            icon={<CopyOutlined />}
            onClick={() => copyToClipboard(clientId, 'client_id')}
            title="Copy Client ID"
          />
        </div>
      ),
    },
    {
      title: 'Client Secret',
      dataIndex: 'client_secret_preview',
      key: 'client_secret_preview',
      width: 180,
      render: (secret) => secret ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Text code style={{ flex: 1, fontSize: '12px' }}>
            {secret}
          </Text>
          <Tooltip title="Client Secret disembunyikan untuk keamanan">
            <LockOutlined style={{ color: '#faad14' }} />
          </Tooltip>
        </div>
      ) : (
        <Tag color="orange">Belum ada</Tag>
      ),
    },
    {
      title: 'RSA Keys',
      key: 'rsa_keys',
      width: 120,
      render: (_, record) => (
        <Space direction="vertical" size="small">
          <Tag color={record.has_private_key ? 'green' : 'red'} style={{ fontSize: '11px' }}>
            Private: {record.has_private_key ? 'Ada' : 'Tidak ada'}
          </Tag>
          <Tag color={record.has_public_key ? 'green' : 'red'} style={{ fontSize: '11px' }}>
            Public: {record.has_public_key ? 'Ada' : 'Tidak ada'}
          </Tag>
        </Space>
      ),
    },
    {
      title: 'OAuth Status',
      dataIndex: 'oauth_status',
      key: 'oauth_status',
      width: 120,
      render: (status) => (
        <Tag color={getStatusColor(status)} style={{ fontSize: '11px' }}>
          {getStatusText(status)}
        </Tag>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'is_active',
      key: 'is_active',
      width: 80,
      render: (active) => (
        <Badge
          status={active ? 'success' : 'error'}
          text={active ? 'Active' : 'Inactive'}
        />
      ),
    },
    {
      title: 'Created',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 120,
      render: (date) => new Date(date).toLocaleDateString('id-ID'),
    },
    {
      title: 'Action',
      key: 'action',
      width: 280,
      render: (_, record) => (
        <Space wrap>
          <Tooltip title="View Details">
            <Button
              size="small"
              icon={<EyeOutlined />}
              onClick={() => viewCredentialDetail(record)}
            />
          </Tooltip>
          <Tooltip title="Upload Public Key">
            <Button
              size="small"
              icon={<UploadOutlined />}
              onClick={() => openUploadKeyModal(record)}
            />
          </Tooltip>
          <Tooltip title="Regenerate Keypair">
            <Button
              size="small"
              icon={<SyncOutlined />}
              onClick={() => openRegenerateKeypairModal(record)}
            />
          </Tooltip>
          <Popconfirm
            title="Regenerate client secret?"
            description="Ini akan membuat client secret baru dan mengganti yang lama."
            onConfirm={() => regenerateClientSecret(record)}
            okText="Ya"
            cancelText="Tidak"
          >
            <Tooltip title="Regenerate Secret">
              <Button
                size="small"
                icon={<ReloadOutlined />}
              />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const activeCredentials = credentials.filter(c => c.is_active);
  const configuredCredentials = credentials.filter(c => c.oauth_status === 'configured');

  if (!memberId) {
    return (
      <div>
        <Card loading={true}>
          <div style={{ textAlign: 'center', padding: '50px 0' }}>
            <h3>Loading Merchant Data...</h3>
            <p>Sedang memuat data merchant...</p>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div>
      <div style={{ marginBottom: '24px' }}>
        <Title level={2}>
          <ApiOutlined style={{ marginRight: 8 }} />
          SNAP BI V2 Credentials
        </Title>
        <Paragraph style={{ color: '#666' }}>
          Kelola kredensial SNAP BI V2 untuk integrasi dengan Bank Indonesia API
        </Paragraph>
      </div>

      {/* Main Alert */}
      <Alert
        message="Informasi SNAP BI V2"
        description={
          <div>
            <p><strong>SNAP BI V2</strong> adalah standar API Bank Indonesia untuk layanan pembayaran:</p>
            <ul>
              <li><strong>Partner ID:</strong> Identifier unik untuk merchant Anda</li>
              <li><strong>Client ID & Secret:</strong> Kredensial OAuth 2.0 untuk authentication</li>
              <li><strong>RSA Keys:</strong> Untuk digital signature pada request API</li>
            </ul>
          </div>
        }
        type="info"
        showIcon
        style={{ marginBottom: '24px' }}
      />

      <Alert
        message="Keamanan Kredensial"
        description="Client secret dan private key bersifat rahasia. Jangan pernah membagikannya kepada pihak ketiga atau menyimpan di client-side code."
        type="warning"
        showIcon
        style={{ marginBottom: '24px' }}
      />

      {/* Stats Cards */}
      <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Total Credentials"
              value={credentials.length}
              prefix={<KeyOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Active"
              value={activeCredentials.length}
              valueStyle={{ color: '#3f8600' }}
              prefix={<CheckCircleOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Terkonfigurasi"
              value={configuredCredentials.length}
              valueStyle={{ color: '#1890ff' }}
              prefix={<SafetyOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Perlu Setup"
              value={credentials.length - configuredCredentials.length}
              valueStyle={{ color: '#faad14' }}
              prefix={<ExclamationCircleOutlined />}
            />
          </Card>
        </Col>
      </Row>

      {/* Main Credentials Table */}
      <Card>
        <div style={{ marginBottom: '16px', display: 'flex', justifyContent: 'space-between' }}>
          <Title level={4}>Daftar Kredensial SNAP BI V2</Title>
          <Space>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleCreateCredential}
              disabled={loading}
            >
              Buat Credential Baru
            </Button>
            <Button
              icon={<ReloadOutlined />}
              onClick={fetchSnapV2Credentials}
              loading={loading}
            >
              Refresh
            </Button>
          </Space>
        </div>

        <Table
          columns={columns}
          dataSource={credentials}
          rowKey="id"
          loading={loading}
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} dari ${total} kredensial`,
          }}
          scroll={{ x: 1200 }}
        />
      </Card>

      {/* Detail Modal */}
      <Modal
        title="Detail Kredensial SNAP BI V2"
        open={detailModalVisible}
        onCancel={() => {
          setDetailModalVisible(false);
          setSelectedCredential(null);
        }}
        footer={[
          <Button key="close" onClick={() => setDetailModalVisible(false)}>
            Tutup
          </Button>
        ]}
        width={700}
      >
        {selectedCredential && (
          <div>
            <Row gutter={[16, 16]}>
              <Col span={12}>
                <div style={{ marginBottom: 16 }}>
                  <Text strong>Partner ID:</Text>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 4 }}>
                    <Input value={selectedCredential.partner_id} readOnly />
                    <Button
                      icon={<CopyOutlined />}
                      onClick={() => copyToClipboard(selectedCredential.partner_id, 'partner_id')}
                    />
                  </div>
                </div>
              </Col>
              <Col span={12}>
                <div style={{ marginBottom: 16 }}>
                  <Text strong>Client ID:</Text>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 4 }}>
                    <Input value={selectedCredential.client_id} readOnly />
                    <Button
                      icon={<CopyOutlined />}
                      onClick={() => copyToClipboard(selectedCredential.client_id, 'client_id')}
                    />
                  </div>
                </div>
              </Col>
            </Row>

            <Row gutter={[16, 16]}>
              <Col span={12}>
                <div style={{ marginBottom: 16 }}>
                  <Text strong>Client Secret:</Text>
                  <Input
                    value={selectedCredential.client_secret_display}
                    readOnly
                    style={{ marginTop: 4 }}
                    type="password"
                  />
                  <Text type="secondary" style={{ fontSize: '12px' }}>
                    Dibagikan untuk keamanan
                  </Text>
                </div>
              </Col>
              <Col span={12}>
                <div style={{ marginBottom: 16 }}>
                  <Text strong>Status:</Text>
                  <div style={{ marginTop: 4 }}>
                    <Badge
                      status={selectedCredential.is_active ? 'success' : 'error'}
                      text={selectedCredential.is_active ? 'Active' : 'Inactive'}
                    />
                  </div>
                </div>
              </Col>
            </Row>

            <Divider />

            <Row gutter={[16, 16]}>
              <Col span={12}>
                <div style={{ marginBottom: 16 }}>
                  <Text strong>RSA Private Key:</Text>
                  <div style={{ marginTop: 4 }}>
                    <Tag color={selectedCredential.has_private_key ? 'green' : 'red'}>
                      {selectedCredential.has_private_key ? 'Tersedia' : 'Tidak tersedia'}
                    </Tag>
                  </div>
                </div>
              </Col>
              <Col span={12}>
                <div style={{ marginBottom: 16 }}>
                  <Text strong>RSA Public Key:</Text>
                  <div style={{ marginTop: 4 }}>
                    <Tag color={selectedCredential.has_public_key ? 'green' : 'red'}>
                      {selectedCredential.has_public_key ? 'Tersedia' : 'Tidak tersedia'}
                    </Tag>
                  </div>
                </div>
              </Col>
            </Row>

            <Row gutter={[16, 16]}>
              <Col span={12}>
                <div style={{ marginBottom: 16 }}>
                  <Text strong>Dibuat:</Text>
                  <div style={{ marginTop: 4 }}>
                    {new Date(selectedCredential.created_at).toLocaleString('id-ID')}
                  </div>
                </div>
              </Col>
              <Col span={12}>
                <div style={{ marginBottom: 16 }}>
                  <Text strong>Terakhir Update:</Text>
                  <div style={{ marginTop: 4 }}>
                    {selectedCredential.updated_at ?
                      new Date(selectedCredential.updated_at).toLocaleString('id-ID') :
                      'Belum pernah'
                    }
                  </div>
                </div>
              </Col>
            </Row>

            </div>
        )}
      </Modal>

      {/* Regenerate Confirmation Modal */}
      <Modal
        title="Konfirmasi Regenerasi Client Secret"
        open={regenerateModalVisible}
        onOk={handleRegenerateSecret}
        onCancel={() => {
          setRegenerateModalVisible(false);
          setSelectedCredential(null);
        }}
        okText="Regenerate"
        cancelText="Batal"
      >
        <Alert
          message="Perhatian!"
          description={
            <div>
              <p>Anda akan melakukan regenerate client secret untuk:</p>
              <ul>
                <li><strong>Partner ID:</strong> {selectedCredential?.partner_id}</li>
                <li><strong>Client ID:</strong> {selectedCredential?.client_id}</li>
              </ul>
              <p><strong>Consequences:</strong></p>
              <ul>
                <li>Client secret yang lama akan tidak valid lagi</li>
                <li>Semua aplikasi yang menggunakan client secret lama perlu diperbarui</li>
                <li>Client secret baru hanya akan ditampilkan sekali ini</li>
              </ul>
            </div>
          }
          type="warning"
          showIcon
        />
      </Modal>

      {/* Create Credential Modal */}
      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <PlusOutlined />
            Buat SNAP BI V2 Credential Baru
          </div>
        }
        open={createModalVisible}
        onCancel={() => {
          setCreateModalVisible(false);
          createForm.resetFields();
        }}
        footer={null}
        width={600}
      >
        <Alert
          message="Informasi Penting"
          description={
            <div>
              <p>• Setelah membuat credential, Anda akan mendapatkan:</p>
              <ul style={{ marginLeft: 20 }}>
                <li>Partner ID (untuk identifikasi merchant)</li>
                <li>Client ID & Client Secret (untuk OAuth 2.0)</li>
                <li>RSA Private Key (untuk digital signature)</li>
                <li>RSA Public Key (untuk verifikasi)</li>
              </ul>
              <p>• Simpan semua kredensial dengan aman, hanya ditampilkan sekali!</p>
            </div>
          }
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
        />

        <Form
          form={createForm}
          layout="vertical"
          onFinish={handleCreateSubmit}
        >
          <Form.Item
            label="Nama Credential"
            name="name"
            rules={[
              { required: true, message: 'Nama credential wajib diisi' },
              { min: 3, message: 'Minimal 3 karakter' },
              { max: 50, message: 'Maksimal 50 karakter' }
            ]}
            help="Nama untuk mengidentifikasi credential ini (contoh: Production API)"
          >
            <Input
              placeholder="e.g., Production API, Development API"
              showCount
              maxLength={50}
            />
          </Form.Item>

          <Form.Item
            label="Deskripsi"
            name="description"
            help="Deskripsi singkat tentang penggunaan credential ini"
          >
            <Input.TextArea
              placeholder="e.g., Digunakan untuk transaksi production"
              autoSize={{ minRows: 2, maxRows: 4 }}
              showCount
              maxLength={200}
            />
          </Form.Item>

          <Form.Item
            label="Callback URL (Opsional)"
            name="callback_url"
            help="URL untuk menerima callback dari SNAP BI. Jika kosong, akan menggunakan default."
          >
            <Input
              placeholder="example.com/callback"
              addonBefore="https://"
            />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button
                onClick={() => {
                  setCreateModalVisible(false);
                  createForm.resetFields();
                }}
              >
                Batal
              </Button>
              <Button
                type="primary"
                htmlType="submit"
                loading={loading}
              >
                Buat Credential
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Upload Public Key Modal */}
      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <UploadOutlined />
            Upload Public Key
          </div>
        }
        open={uploadKeyModalVisible}
        onCancel={() => {
          setUploadKeyModalVisible(false);
          setSelectedCredential(null);
          uploadKeyForm.resetFields();
        }}
        footer={null}
        width={700}
      >
        <Alert
          message="Informasi Upload Public Key"
          description={
            <div>
              <p>Upload public key Anda sendiri jika Anda sudah memiliki RSA keypair yang digunakan oleh aplikasi eksternal.</p>
              <p><strong>Format yang didukung:</strong></p>
              <ul>
                <li>RSA Public Key dalam format PEM (PKCS#8)</li>
                <li>Harus dimulai dengan <code>-----BEGIN PUBLIC KEY-----</code></li>
                <li>Harus diakhiri dengan <code>-----END PUBLIC KEY-----</code></li>
              </ul>
            </div>
          }
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
        />

        {selectedCredential && (
          <div style={{ marginBottom: 16 }}>
            <Text strong>Partner ID:</Text> <Text code>{selectedCredential.partner_id}</Text>
          </div>
        )}

        <Form
          form={uploadKeyForm}
          layout="vertical"
          onFinish={handleUploadPublicKey}
        >
          <Form.Item
            label="Public Key (PEM Format)"
            name="public_key"
            rules={[
              { required: true, message: 'Public key wajib diisi' },
              {
                validator: (_, value) => {
                  if (value && !value.includes('-----BEGIN PUBLIC KEY-----')) {
                    return Promise.reject('Public key harus dalam format PEM yang valid');
                  }
                  return Promise.resolve();
                }
              }
            ]}
          >
            <Input.TextArea
              placeholder={`-----BEGIN PUBLIC KEY-----
MIIBIjANBgkqhkiG9w0BAQEFAAOCAQ8AMIIBCgKCAQEA...
-----END PUBLIC KEY-----`}
              autoSize={{ minRows: 8, maxRows: 15 }}
              style={{ fontFamily: 'monospace', fontSize: '11px' }}
            />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button
                onClick={() => {
                  setUploadKeyModalVisible(false);
                  setSelectedCredential(null);
                  uploadKeyForm.resetFields();
                }}
              >
                Batal
              </Button>
              <Button
                type="primary"
                htmlType="submit"
                loading={loading}
                icon={<UploadOutlined />}
              >
                Upload Public Key
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Regenerate Keypair Confirmation Modal */}
      <Modal
        title="Konfirmasi Regenerate RSA Keypair"
        open={regenerateKeypairModalVisible}
        onOk={handleRegenerateKeypair}
        onCancel={() => {
          setRegenerateKeypairModalVisible(false);
          setSelectedCredential(null);
        }}
        okText="Regenerate Keypair"
        cancelText="Batal"
        okButtonProps={{ icon: <SyncOutlined /> }}
      >
        <Alert
          message="Perhatian!"
          description={
            <div>
              <p>Anda akan melakukan regenerate RSA keypair untuk:</p>
              <ul>
                <li><strong>Partner ID:</strong> {selectedCredential?.partner_id}</li>
                <li><strong>Client ID:</strong> {selectedCredential?.client_id}</li>
              </ul>
              <p><strong>Konsekuensi:</strong></p>
              <ul>
                <li>Private key dan public key yang lama akan tidak valid lagi</li>
                <li>Semua aplikasi yang menggunakan private key lama perlu diperbarui</li>
                <li>Private key baru hanya akan ditampilkan sekali setelah regenerate</li>
              </ul>
            </div>
          }
          type="warning"
          showIcon
        />
      </Modal>
    </div>
  );
};

export default MerchantSnapV2Credentials;